import React, { useState, useEffect } from 'react';
import { verifyOrganizer } from '../services/api';

// Email verification landing page for organizer signup.
// Reached via /verify-organizer?token=<hex-token> in the verification email.
//
// On mount, automatically submits the token to POST /auth/verify-organizer.
// On success, the backend sets the auth cookie and returns the participant —
// onVerified() is called with the participant so App.js can transition
// directly into the logged-in state.

function VerifyOrganizer({ onVerified, onBackToLogin }) {
    const token = new URLSearchParams(window.location.search).get('token');

    const [status, setStatus] = useState('verifying'); // 'verifying' | 'success' | 'error'
    const [error,  setError]  = useState('');

    useEffect(() => {
        if (!token) {
            setStatus('error');
            setError('This verification link is missing its token. Please use the link from your email.');
            return;
        }

        verifyOrganizer(token)
            .then(res => {
                setStatus('success');
                // Small delay so the user sees the success message before
                // being transitioned into the app.
                setTimeout(() => onVerified(res.data.participant), 1500);
            })
            .catch(err => {
                setStatus('error');
                setError(err.response?.data?.error || 'Something went wrong. Please try signing up again.');
            });
    }, [token, onVerified]);

    return (
        <div className="min-h-screen bg-[#13294f] flex items-center justify-center px-4">
            <div className="w-full max-w-md">
                <div className="text-center mb-8">
                    <div className="flex items-center justify-center gap-4 mb-4">
                        <img src="/favicon.svg" alt="" className="h-20 w-20 flex-shrink-0" />
                        <div className="text-5xl font-black text-white tracking-tight">
                            Hebert<span className="text-orange-500">Hoops</span>
                        </div>
                    </div>
                    <p className="text-gray-400">Organizer account setup</p>
                </div>
                <div className="bg-gray-900 rounded-2xl p-8 border border-gray-800">
                    {status === 'verifying' && (
                        <div className="text-center space-y-4">
                            <div className="text-gray-400 text-sm">Verifying your email…</div>
                            <div className="flex justify-center">
                                <div className="w-6 h-6 border-2 border-orange-500 border-t-transparent rounded-full animate-spin" />
                            </div>
                        </div>
                    )}

                    {status === 'success' && (
                        <div className="space-y-4">
                            <div className="bg-green-900/40 border border-green-600 text-green-200 px-4 py-3 rounded-lg text-sm">
                                <p className="font-bold mb-1">Email verified!</p>
                                <p>Your organizer account is active. Taking you to the app…</p>
                            </div>
                        </div>
                    )}

                    {status === 'error' && (
                        <div className="space-y-6">
                            <div className="bg-red-900/50 border border-red-500 text-red-300 px-4 py-3 rounded-lg text-sm">
                                {error}
                            </div>
                            <button
                                onClick={onBackToLogin}
                                className="w-full bg-orange-500 hover:bg-orange-600 text-white font-bold py-3 rounded-lg transition-colors"
                            >
                                Back to sign in
                            </button>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

export default VerifyOrganizer;